// ============================================================================
// Masterclass « Manager IA » — planning et copie de la LP (/mc, /mc/ty, /mc/hc)
//
//   Session récurrente : TOUS LES JEUDIS 12h30 (Europe/Paris), 1 heure,
//   gratuite, EN DIRECT et SANS REPLAY. L'absence de replay n'est pas un
//   argument décoratif : c'est la rareté vraie du funnel, elle doit rester
//   visible partout (cf. « Ads Masterclass » §5 — les CTA intemporels).
//
//   Contraintes de copie héritées des documents de travail :
//     • Meta / attributs personnels — interdiction d'affirmer qu'on connaît la
//       situation du lecteur. Toute la douleur est donc formulée à la
//       TROISIÈME PERSONNE (« les managers d'ETI passent… »), jamais « vos
//       comptes-rendus mangent vos soirées ». C'est un motif de rejet classique.
//     • On vend l'ÉVÉNEMENT et l'ARTEFACT (« vous repartez avec l'assistant »),
//       pas la transformation.
//     • Aucune rareté fabriquée : les seules contraintes affichées sont réelles
//       (le direct, l'absence de replay).
// ============================================================================

// ============================================================================
// 1. Planning — prochain jeudi 12h30 (Europe/Paris)
// ============================================================================

const TZ = "Europe/Paris";
const WEEKDAY = 4; // jeudi (0 = dimanche)
const HOUR = 12;
const MINUTE = 30;

/**
 * Durée ANNONCÉE de la session, en minutes.
 * ⚠️ 60 min sur la LP (décision produit : « ça dure une heure »), alors que la
 * trame interne du webinaire court sur 80 min + débordement Q&A. Le programme
 * public est donc volontairement donné en grandes lignes, sans minutage.
 */
export const DURATION_MIN = 60;

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
};

/** Éclate un instant en heure murale parisienne (gère l'heure d'été). */
function parisParts(at: number) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: TZ,
    hour12: false,
    weekday: "short",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(new Date(at));

  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "0";
  return {
    year: Number(get("year")),
    month: Number(get("month")) - 1,
    day: Number(get("day")),
    // certains moteurs rendent "24" pour minuit → on ramène dans [0,23]
    hour: Number(get("hour")) % 24,
    minute: Number(get("minute")),
    second: Number(get("second")),
    weekday: WEEKDAY_INDEX[get("weekday")] ?? 0,
  };
}

/**
 * Décalage Paris ↔ UTC (ms) à l'instant donné : l'écart entre l'heure murale
 * parisienne lue comme si elle était UTC, et l'instant réel.
 */
function parisOffsetMs(at: number): number {
  const p = parisParts(at);
  return (
    Date.UTC(p.year, p.month, p.day, p.hour, p.minute, p.second) - at
  );
}

/**
 * Instant (ms epoch) du prochain jeudi 12h30 Paris. Le jeudi même, on continue
 * d'afficher la session en cours jusqu'à sa fin — après quoi on bascule sur la
 * semaine suivante.
 */
export function nextSessionAt(now: number = Date.now()): number {
  const w = parisParts(now);

  let delta = (WEEKDAY - w.weekday + 7) % 7;
  if (delta === 0) {
    const nowMin = w.hour * 60 + w.minute;
    if (nowMin >= HOUR * 60 + MINUTE + DURATION_MIN) delta = 7;
  }

  // Heure murale visée, lue comme UTC, puis corrigée du décalage parisien.
  const wallAsUtc = Date.UTC(w.year, w.month, w.day + delta, HOUR, MINUTE, 0, 0);
  return wallAsUtc - parisOffsetMs(wallAsUtc);
}

/** true si la session est en cours (entre 12h30 et 12h30 + durée). */
export function isLiveNow(now: number = Date.now()): boolean {
  const start = nextSessionAt(now);
  return now >= start && now < start + DURATION_MIN * 60_000;
}

/** « jeudi 4 septembre » — date longue, sans l'heure. */
export function formatSessionDate(at: number): string {
  return new Intl.DateTimeFormat("fr-FR", {
    timeZone: TZ,
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date(at));
}

/** Libellé court réutilisé partout : « Jeudi 12h30 ». */
export const SLOT_LABEL = "Jeudi 12h30";

/**
 * « ce jeudi » tant que la session tombe dans la semaine en cours, « jeudi
 * prochain » une fois celle-ci passée. L'imminence porte plus que la date : on
 * vend une présence à heure fixe, pas un contenu.
 */
export function proximityLabel(now: number = Date.now()): string {
  const remaining = nextSessionAt(now) - now;
  return remaining < 7 * 86_400_000 ? "ce jeudi" : "jeudi prochain";
}

/**
 * Groupe WhatsApp de la masterclass.
 * Ce n'est pas une URL en dur : c'est un slug de redirection géré depuis
 * /admin, parce qu'un lien d'invitation WhatsApp expire et se régénère. La
 * valeur ici ne change jamais, seule la destination bouge.
 * Le bouton ne s'affiche que si la redirection existe — sinon on enverrait les
 * inscrits sur un 404.
 */
export const WHATSAPP_SLUG = "masterclass-whatsapp";
export const WHATSAPP_PATH = `/go/${WHATSAPP_SLUG}`;

export const WHATSAPP_CTA = {
  step: "Dernière étape",
  title: "Le groupe WhatsApp de la session",
  sticky: "Rejoindre le groupe WhatsApp",
  body: "On y envoie le lien de connexion et un rappel juste avant le direct. Sans replay, c’est ce qui fait la différence entre s’inscrire et être là.",
  cta: "Rejoindre le groupe WhatsApp",
};

/** Titre de l'événement, réutilisé dans les liens d'agenda. */
export const SESSION_TITLE = "Masterclass Manager IA — Hecto Forma";

const pad = (n: number) => String(n).padStart(2, "0");

/** Format compact UTC attendu par Google Agenda : 20260903T103000Z */
function gcalStamp(at: number): string {
  const d = new Date(at);
  return (
    `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}` +
    `T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}00Z`
  );
}

/**
 * Liens « ajouter à mon agenda ». Un webinaire sans replay se joue sur la
 * présence : le créneau doit atterrir dans l'agenda dès la confirmation.
 * Ce sont de simples URL (aucun téléchargement de fichier .ics à gérer).
 */
export function calendarLinks(startAt: number, joinUrl = "") {
  const endAt = startAt + DURATION_MIN * 60_000;
  const details = joinUrl
    ? `Lien de connexion : ${joinUrl}`
    : "Le lien de connexion est envoyé par email avant le début.";

  const google = new URL("https://calendar.google.com/calendar/render");
  google.searchParams.set("action", "TEMPLATE");
  google.searchParams.set("text", SESSION_TITLE);
  google.searchParams.set("dates", `${gcalStamp(startAt)}/${gcalStamp(endAt)}`);
  google.searchParams.set("details", details);
  if (joinUrl) google.searchParams.set("location", joinUrl);

  const outlook = new URL(
    "https://outlook.office.com/calendar/0/deeplink/compose"
  );
  outlook.searchParams.set("path", "/calendar/action/compose");
  outlook.searchParams.set("rru", "addevent");
  outlook.searchParams.set("subject", SESSION_TITLE);
  outlook.searchParams.set("startdt", new Date(startAt).toISOString());
  outlook.searchParams.set("enddt", new Date(endAt).toISOString());
  outlook.searchParams.set("body", details);

  return { google: google.toString(), outlook: outlook.toString() };
}


// ============================================================================
// 2. Copie de la LP — courte, frontale, une idée par bloc
//
//   Principes tenus (Le Bon / Bernays, cf. « Webinaire » §4) :
//     • AFFIRMATION — on affirme, on ne démontre pas. Aucun conditionnel dans
//       la promesse : « vous repartez avec », jamais « vous pourriez ».
//     • RÉPÉTITION — « la moitié de cette charge, vous l'avez déjà faite » et
//       « pas de replay » reviennent mot pour mot, sans variation stylistique.
//     • IMAGES — une seule image par bloc. Deux s'annulent.
//     • ÉVÉNEMENT — on vend une présence à heure fixe et l'artefact qu'on
//       emporte, jamais la transformation.
//   Et les deux lignes rouges : aucune rareté fabriquée, aucune culpabilisation.
// ============================================================================

// ---------------------------------------------------------------------------
// 2.1 — Les promesses (test A/B `mc-headline-v2`)
//
//   ⚠️ DEUX RÈGLES DE COPIE, valables sur TOUTE la page.
//   1. On vend la GRANDE VISION, jamais le mécanisme. Personne ne rêve d'un
//      compte-rendu de réunion : l'avatar veut reprendre le contrôle de son
//      agenda, tenir les objectifs de son équipe, ne pas se faire dépasser.
//   2. FORMES ACTIVES UNIQUEMENT. On dit ce que le lecteur gagne et ce qu'il
//      fait — « prenez une heure, gagnez quatre heures ». Jamais de tournure
//      négative ou passive : « vous ne montez pas en compétence » ne veut rien
//      dire pour personne.
// ---------------------------------------------------------------------------
export const HEADLINES: Record<string, string> = {
  // Champion du premier tour (5,9 % de clic CTA, les 4 autres entre 2 et 4 %).
  // Le levier qui vend aussi l'intra plutôt que l'individuel.
  equipe: "Tenez les objectifs de l’année avec la même équipe.",
  // Tirés de la VSL, 2e tour :
  // · Le titre de la VSL. Chute d'abord (« n'atteignent plus »), bascule
  //   ensuite : la curiosité porte jusqu'au sous-titre.
  objectifs: "Certains managers n’atteignent plus leurs objectifs. Ils les dépassent.",
  // · « J'ai déjà testé » — l'objection n°3 de la VSL, retournée en accroche.
  //   Le trafic vient surtout des pubs ChatGPT : il utilise déjà l'IA.
  dejateste: "Vous utilisez déjà l’IA. Et votre semaine n’a pas bougé d’une heure.",
  // · La scène du « même lundi, autrement » : le contrôle, pas le temps.
  lundi: "Lundi, 8h12. Pour une fois, votre journée vous appartient.",
  // · Le plus direct : on nomme l'offre. Aucune promesse détournée, le lecteur
  //   sait en une seconde ce qu'on lui propose.
  masterclass: "Masterclass IA pour managers : confiez à l’IA ce qui mange votre semaine.",
};

// ---------------------------------------------------------------------------
// 2.2 — Les sous-titres (test A/B `mc-tagline`). Trois phrases maximum.
// ---------------------------------------------------------------------------
export const TAGLINES: Record<string, string> = {
  control:
    "Une heure en direct, jeudi 12h30. Vous repartez avec l’IA qui écrit vos comptes-rendus, met à jour vos reportings et envoie vos relances. Dans vos outils, sans coder.",
  preuve:
    "Une heure en direct, jeudi 12h30. Quatre heures récupérées chaque semaine, mesurées — dans vos outils, sans coder.",
  dsi: "Une heure en direct, jeudi 12h30. On construit dans vos outils imposés, avec vos contraintes IT. Pas à côté.",
};

// ---------------------------------------------------------------------------
// 2.3 — Les CTA (test A/B `mc-cta`).
// ---------------------------------------------------------------------------
export const CTAS: Record<string, string> = {
  reserver: "Réserver ma place",
  gratuit: "Je réserve ma place — c’est gratuit",
  jeudi: "Réserver ma place pour jeudi 12h30",
};

// ---------------------------------------------------------------------------
// 2.4 — La preuve sociale sous le sous-titre (test A/B `mc-preuve`).
//   Chiffres et secteurs repris du script VSL (validés par Hecto Forma).
//   ⚠️ Aucun nom de client tant que l'autorisation écrite manque (cf. REFERENCES).
//   `aucune` = rien d'affiché : c'est la page telle qu'elle tournait.
// ---------------------------------------------------------------------------
export const SOCIAL_PROOF: Record<string, string | null> = {
  aucune: null,
  chiffres: "Plus de 3 000 collaborateurs formés dans 35 entreprises.",
  secteurs:
    "Déjà déployé chez une mutuelle nationale, un opérateur gazier, deux enseignes de retail et deux ESN.",
};

/** Micro-copie sous le CTA. */
export const CTA_HINT = "1 heure · gratuit · en direct · aucun replay";

/** Bandeau d'imminence (haut de page + barres flottantes). */
export const LIVE_BADGE = "En direct";

// ---------------------------------------------------------------------------
// 2.4 — Avant / après, par tâche qui revient.
//   Des DURÉES, jamais des euros : à ce stade le lecteur achète du temps rendu,
//   le gain financier ne lui parle pas. Ordre de grandeur tenu : ÷ 5 environ.
// ---------------------------------------------------------------------------
export const USE_CASES: {
  task: string;
  before: string;
  after: string;
  note: string;
}[] = [
  {
    task: "Compte-rendu de réunion",
    before: "5 h / mois",
    after: "1 h / mois",
    note: "Structuré, avec les décisions, les actions et les responsables.",
  },
  {
    task: "Reporting hebdomadaire",
    before: "8 h / mois",
    after: "1 h 30 / mois",
    note: "Mis à jour tout seul depuis vos données. Vous relisez, vous validez.",
  },
  {
    task: "Relances et coordination",
    before: "4 h / mois",
    after: "45 min / mois",
    note: "Rédigées et envoyées en quelques secondes.",
  },
  {
    task: "Slides de comité",
    before: "6 h / mois",
    after: "1 h 15 / mois",
    note: "Construites pendant vos heures de travail. Pas le week-end.",
  },
  {
    task: "Synthèse de dossiers",
    before: "5 h / mois",
    after: "1 h / mois",
    note: "Lus, résumés, et les points d’attention remontés.",
  },
];

export const USE_CASES_EYEBROW = "Les tâches qui reviennent";
export const USE_CASES_TITLE = "Avant. Après.";
/** Preuve terrain, en durées. Aucun montant : cf. règle ci-dessus. */
export const USE_CASES_PROOF =
  "Sur 20 rapports par mois, un studio de production est passé de 2 h 40 à 15 minutes par rapport.";

// ---------------------------------------------------------------------------
// 2.5 — La promesse en TERMS (Fladlien), en formes actives.
//   T Time · E Effort · R Routine · M Money · S Status
//   Money/Anchor : le GRAND NOMBRE D'ABORD (2 000 €), le gratuit ensuite.
// ---------------------------------------------------------------------------
export const TERMS_TITLE = "Une heure pour gagner quatre heures par semaine.";

export const TERMS_PROMISE: { levier: string; icon: string; line: string }[] = [
  {
    levier: "Temps",
    icon: "clock",
    line: "Prenez une heure ce jeudi. Gagnez quatre heures par semaine, toute l’année.",
  },
  {
    levier: "Effort",
    icon: "spark",
    line: "On vous explique simplement. Vous appliquez dès le lendemain, sans coder.",
  },
  {
    levier: "Routine",
    icon: "repeat",
    line: "Quelques réglages dans votre quotidien. Rien à installer, rien à migrer.",
  },
  {
    levier: "Argent",
    icon: "tag",
    line: "Des entreprises achètent cette conférence plus de 2 000 €. Jeudi, elle est gratuite.",
  },
  {
    levier: "Statut",
    icon: "rise",
    line: "Devenez le manager dont l’équipe tient ses objectifs. Ça se voit d’en haut.",
  },
];

// ---------------------------------------------------------------------------
// 2.6 — Les bonus : on annonce qu'il y en a, on ne dit PAS lesquels.
//   Détailler le contenu, c'est donner une raison de ne pas venir.
// ---------------------------------------------------------------------------
export const BONUS_TEASER = {
  eyebrow: "En fin de session",
  title: "Trois documents remis en direct.",
  body: "On ne les annonce pas à l’avance. Ils sont remis à la fin, à ceux qui sont là — et ils servent surtout à ceux qui devront en parler en interne.",
};

// ---------------------------------------------------------------------------
// 2.7 — Ce qui se passe dans l'heure (timeline).
// ---------------------------------------------------------------------------
export const PROGRAM_TITLE = "Ce qui se passe dans l’heure.";

export const PROGRAM: { icon: string; title: string; detail: string }[] = [
  {
    icon: "gauge",
    title: "On mesure ce que votre semaine coûte",
    detail: "Le chiffre sort de vos réponses, il n’est pas annoncé.",
  },
  {
    icon: "switch",
    title: "On vous montre pourquoi l’IA ne vous a rien rendu",
    detail: "Vous vous êtes arrêté au chat. La bascule, c’est l’agent.",
  },
  {
    icon: "build",
    title: "On en installe un, devant vous",
    detail: "Sur vos outils, avec vos contraintes. Il tourne le soir même.",
  },
  {
    icon: "chat",
    title: "On répond à vos questions",
    detail: "On reste tant qu’il y en a.",
  },
];

// ---------------------------------------------------------------------------
// 2.8 — Pour qui + les objections : une seule section, en accordéon.
// ---------------------------------------------------------------------------
export const AUDIENCE = {
  title: "C’est pour les managers en entreprise.",
  intro:
    "Vous encadrez une équipe, vos outils sont imposés, votre DSI arbitre et vos données ne sortent pas. C’est exactement le terrain sur lequel on construit — et c’est là que toutes les démos échouent.",
};

export const OBJECTIONS: { q: string; a: string }[] = [
  {
    q: "Mon IT ne me laissera jamais faire.",
    a: "La contrainte est une étape, pas un mur. On vous montre comment arriver préparé — c’est ce qui fait dire oui à une DSI.",
  },
  {
    q: "Les données de l’entreprise, c’est trop risqué.",
    a: "On trace la frontière à l’écran, en trois couleurs : ce qui sort, ce qui ne sort pas, ce qui dépend de votre licence.",
  },
  {
    q: "On a déjà Copilot.",
    a: "Tant mieux, c’est l’environnement dans lequel on construit. La licence qui voit vos données d’entreprise n’a rien à voir avec le chat gratuit.",
  },
  {
    q: "On a déjà eu une formation IA.",
    a: "Celle-ci se mesure : un chrono avant, un chrono après. Vous repartez avec un assistant qui tourne, pas avec des slides.",
  },
  {
    q: "Je ne peux pas jeudi midi.",
    a: "Il n’y a pas de replay : la construction se fait en direct, sur vos questions. Mais la session a lieu chaque jeudi — réservez la semaine suivante.",
  },
];

/**
 * Références clients.
 * ⚠️ VIDE PAR DÉFAUT — VOLONTAIRE. L'affichage public d'une marque cliente
 * suppose une autorisation écrite préalable. La mention textuelle est moins
 * exposée que le logo. Cluster conformité disponible : Conseil d'État,
 * BNP Paribas, GRDF, SII, Bartle, Groupe Agrica, Ayming.
 */
export const REFERENCES: string[] = [];

// ---------------------------------------------------------------------------
// 2.9 — Clôture : le créneau, rien d'autre. Pas de mise en scène du choix.
// ---------------------------------------------------------------------------
export const CLOSING = {
  title: "Jeudi 12h30. Une heure. Sans replay.",
  body: "La construction se fait en direct, sur vos outils et vos questions. Elle ne sera pas rejouée.",
};

// ---------------------------------------------------------------------------
// 2.10 — Confirmation d'inscription (/mc/ty)
// ---------------------------------------------------------------------------
export const THANK_YOU = {
  badge: "Place confirmée ✅",
  title: "C’est réservé.",
  prep: [
    [
      "Une réunion récurrente",
      "celle dont vous faites le compte-rendu chaque semaine. C’est sur elle qu’on va se greffer en direct.",
    ],
    [
      "Un chiffre",
      "le temps que prend ce compte-rendu aujourd’hui, de la fin de la réunion à l’envoi. Approximatif suffit.",
    ],
    ["Vos outils", "plutôt Microsoft 365 (Teams, Outlook, SharePoint) ou Google Workspace ?"],
  ] as [string, string][],
  reminder:
    "Bloquez l’heure dans votre agenda — la construction se fait en direct et ne sera pas rejouée.",
};

// ---------------------------------------------------------------------------
// 2.11 — Hors cible (/mc/hc)
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// 2.11 — Hors cible (/mc/hc)
//   La session est construite pour des managers en poste, mais on ne ferme la
//   porte à personne : le lien est donné. Ce qui change, c'est le suivi — pas
//   d'accès à l'offre entreprise, et surtout AUCUNE information transmise aux
//   régies depuis cette page (cf. @/lib/ad-tracking).
// ---------------------------------------------------------------------------
export const HORS_CIBLE = {
  badge: "Vous êtes inscrit ✅",
  title: "Votre lien est là.",
  body: [
    "Cette masterclass est construite pour des managers en poste, avec les contraintes qui vont avec : outils imposés, DSI, données d’entreprise. D’après vos réponses, ce n’est pas exactement votre situation aujourd’hui — mais la porte n’est pas fermée pour autant.",
    "Venez, prenez ce qui vous sert. La méthode s’applique à n’importe quel travail qui se répète, et l’assistant qu’on construit en direct, vous repartirez avec.",
  ],
  reminder:
    "Une heure en direct, sans replay. Notez le créneau : la construction ne sera pas rejouée.",
};
